import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../useUser';

interface ProductionDashboardMetrics {
  workOrders: {
    total: number;
    inProgress: number;
    completed: number;
    pending: number;
  };
  jobCards: {
    total: number;
    active: number;
    completed: number;
    paused: number;
  };
  workers: {
    total: number;
    active: number;
    idle: number;
  };
  efficiency: {
    production: number;
    timeUtilization: number;
    qualityPass: number;
  };
  departmentPerformance: Array<{
    department: string;
    efficiency: number;
    jobCards: number;
  }>;
  recentActivities: Array<{
    id: string | number;
    title: string;
    description: string;
    timestamp: string;
    type: string;
  }>;
}

interface UseProductionDashboardMetricsResult {
  metrics: ProductionDashboardMetrics | null;
  isLoading: boolean;
  error: Error | null;
}

export const useProductionDashboardMetrics = (): UseProductionDashboardMetricsResult => {
  const { user } = useUser();
  const [metrics, setMetrics] = useState<ProductionDashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProductionMetrics = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        try {
          // Example of fetching work orders summary
          const { data: workOrdersData, error: workOrdersError } = await supabase
            .from('work_orders')
            .select('*')
            .eq('is_deleted', false);
  
          if (workOrdersError) throw workOrdersError;
  
          // Example of fetching job cards summary
          const { data: jobCardsData, error: jobCardsError } = await supabase
            .from('job_cards')
            .select('*')
            .eq('is_deleted', false);
  
          if (jobCardsError) throw jobCardsError;
        } catch (error) {
          // Continue with mock data even if queries fail
        }

        // Simulate metrics
        const mockMetrics: ProductionDashboardMetrics = {
          workOrders: {
            total: 38,
            inProgress: 12,
            completed: 20,
            pending: 6,
          },
          jobCards: {
            total: 57,
            active: 18,
            completed: 32,
            paused: 7,
          },
          workers: {
            total: 24,
            active: 16,
            idle: 8,
          },
          efficiency: {
            production: 87,
            timeUtilization: 78,
            qualityPass: 92,
          },
          departmentPerformance: [
            { department: 'Assembly', efficiency: 91, jobCards: 22 },
            { department: 'Machining', efficiency: 85, jobCards: 18 },
            { department: 'Testing', efficiency: 76, jobCards: 10 },
            { department: 'Finishing', efficiency: 89, jobCards: 7 },
          ],
          recentActivities: [
            {
              id: '1',
              title: 'Work Order Completed',
              description: 'Work Order #WO-2023-0168 was completed',
              timestamp: new Date(Date.now() - 2600000).toISOString(),
              type: 'workOrder',
            },
            {
              id: '2',
              title: 'Job Card Assigned',
              description: 'Job Card #JC-2023-0742 assigned to John D.',
              timestamp: new Date(Date.now() - 7200000).toISOString(),
              type: 'jobCard',
            },
            {
              id: '3',
              title: 'QC Approval',
              description: 'Quality check passed for Batch #B-2023-0095',
              timestamp: new Date(Date.now() - 86400000).toISOString(),
              type: 'qc',
            },
            {
              id: '4',
              title: 'Production Issue',
              description: 'Machine M-1042 reported downtime',
              timestamp: new Date(Date.now() - 172800000).toISOString(),
              type: 'issue',
            }
          ],
        };

        setMetrics(mockMetrics);
      } catch (err) {
        console.error('Error fetching production dashboard metrics:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProductionMetrics();
  }, [user]);

  return { metrics, isLoading, error };
};
